/**
 * Snowplow events for the session-wide observe capture.
 *
 * Filed under the consent category so the capture counts sit beside the consent counts they are
 * compared with.
 */
import { trackStructEvent } from "@snowplow/browser-tracker";

import { isCaptureForbidden } from "@/components/AIAutomation/capture-exclusions";
import {
  type CaptureEligibilityEvent,
  type CaptureEligibilityInput,
  type CaptureEligibilityReason,
} from "@/types/observeCapture";
import {
  Day1EventAction,
  Day1EventCategory,
  Day1EventLabel,
} from "@/types/snowplow-events";

const category = Day1EventCategory.AiJourneyConsent;

/**
 * Returns the first gate that blocks capture, in a fixed precedence, or `CAPTURE-ELIGIBLE` when
 * no gate blocks. Reads `isCaptureForbidden`, not `mayCaptureStep`, which also fails when consent
 * is not granted.
 */
export const resolveCaptureEligibilityReason = ({
  isEnabled,
  hasBridge,
  stepId,
}: CaptureEligibilityInput): CaptureEligibilityReason => {
  if (!isEnabled) return Day1EventLabel.CaptureFlagOff;
  if (!hasBridge) return Day1EventLabel.CaptureNoBridge;
  if (isCaptureForbidden(stepId)) return Day1EventLabel.CaptureStepForbidden;
  return Day1EventLabel.CaptureEligible;
};

/**
 * Gives the label and the property of the CAPTURE-ELIGIBILITY event for the input. The label is
 * the reason and the property is the bridge presence, so bridge coverage stays visible when
 * `CAPTURE-FLAG-OFF` wins.
 */
export const buildCaptureEligibilityEvent = (
  input: CaptureEligibilityInput
): CaptureEligibilityEvent => ({
  label: resolveCaptureEligibilityReason(input),
  property: input.hasBridge ? "true" : "false",
});

/** Whether two CAPTURE-ELIGIBILITY events have the same label and the same property. */
export const isSameCaptureEligibilityEvent = (
  lastSent: CaptureEligibilityEvent | undefined,
  next: CaptureEligibilityEvent
): boolean =>
  lastSent?.label === next.label && lastSent.property === next.property;

/** Sends the CAPTURE-ELIGIBILITY event. */
export const emitCaptureEligibility = (
  event: CaptureEligibilityEvent
): void => {
  trackStructEvent({
    category,
    action: Day1EventAction.CaptureEligibility,
    ...event,
  });
};

/**
 * Sends one CAPTURE-STARTED event.
 * The event repeats on each capture loop start, so count distinct users.
 */
export const emitCaptureStarted = (): void => {
  trackStructEvent({
    category,
    action: Day1EventAction.CaptureStarted,
  });
};
